import moment from "moment";
import {BufferWriter, Writer} from "protobufjs";
import {Customer} from "../../../domain/model/customer";
import {FrontDate} from "../../../domain/model/frontDate";
import {
    ICustomerRepository,
    TCreateCustomerRequest,
    TCustomerCountByOccupationRequest,
    TUpdateCustomerParams,
} from "../../../domain/repository/customer_repository";
import {IApiClient} from "../client";
import {api, common, entity} from "../rpc/api";
import {getToken} from "./bind_token";
import {CustomerConvertResponse, CustomerConvertResponseList,} from "./customer_api_convert";
import CustomerID = entity.CustomerID;
import CustomerIDList = entity.CustomerIDList;
import CustomerList = entity.SafeCustomerList;
import SearchCustomerRequest = api.SearchCustomerRequest;
import SearchCustomerCountRequest = api.SearchCustomerCountRequest;
import UpdateCustomerRequest = api.UpdateCustomerRequest;
import CreateCustomerRequest = api.CreateCustomerRequest;
import AgenticSearchRequest = api.AgenticSearchRequest;

import Empty = common.Empty;
import Pager = common.Pager;
import Count = common.Count;

import CustomerCountByOccupationRequest = api.CustomerCountByOccupationRequest;

class CustomerAPI implements ICustomerRepository {
    private static birthdateConvertToString = (
        frontDate: FrontDate | undefined
    ): string => {
        if (!frontDate) {
            return ``;
        } else {
            return frontDate.year === "" && frontDate.month === "" && frontDate.day
                ? ``
                : moment()
                    .year(Number(frontDate.year.toString()))
                    .month(Number(frontDate.month.toString()) - 1)
                    .date(Number(frontDate.day.toString()))
                    .format("YYYY-MM-DD")
                    .toString();
        }
    };

    constructor(readonly apiClient: IApiClient) {
    }

    public getAll(): Promise<Customer[]> {
        return new Promise<Customer[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: Empty = new Empty();
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetAll",
                        token,
                        Empty.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CustomerList = CustomerList.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponseList =
                        CustomerConvertResponseList.from(res);
                    const items: Customer[] = resConverted.items.map(
                        (item: CustomerConvertResponse): Customer => {
                            return Customer.from(item);
                        }
                    );
                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getAllByIDs(ids: string[]): Promise<Customer[]> {
        return new Promise<Customer[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CustomerIDList = new CustomerIDList();
                    req.ids = ids;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetAllByIDs",
                        token,
                        CustomerIDList.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CustomerList = CustomerList.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponseList =
                        CustomerConvertResponseList.from(res);
                    const items: Customer[] = resConverted.items.map(
                        (item: CustomerConvertResponse): Customer => {
                            return Customer.from(item);
                        }
                    );
                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getAllBySearch(
        query: string,
        singleSkillTagIds: string[],
        multiSkillTagIds: string[],
        occupationSkillTagCategory: entity.OccupationSkillTag.Category,
        occupationSkillTagId: string,
        gender: entity.User.Gender,
        age: api.CustomerAgeSearchType,
        workIntention: entity.Customer.WorkIntention,
        fullTimePossibility: entity.Customer.FullTimePossibility,
        page: number,
        limit: number
    ): Promise<Customer[]> {
        return new Promise<Customer[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: SearchCustomerRequest = new SearchCustomerRequest();
                    const pager: Pager = new Pager();
                    pager.page = page;
                    pager.offset = limit;
                    req.pager = pager;
                    req.query = query;
                    req.singleSkillTagIDs = singleSkillTagIds;
                    req.multiSkillTagIDs = multiSkillTagIds;
                    req.occupationSkillTagCategory = occupationSkillTagCategory;
                    req.occupationSkillTagID = occupationSkillTagId;
                    req.gender = gender;
                    req.age = age;
                    req.workIntention = workIntention;
                    req.fullTimePossibility = fullTimePossibility;

                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetAllBySearch",
                        token,
                        SearchCustomerRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CustomerList = CustomerList.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponseList =
                        CustomerConvertResponseList.from(res);
                    const items: Customer[] = resConverted.items.map(
                        (item: CustomerConvertResponse): Customer => {
                            return Customer.from(item);
                        }
                    );
                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public get(customerID: string): Promise<Customer> {
        return new Promise<Customer>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CustomerID = new CustomerID();
                    req.id = customerID;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/Get",
                        token,
                        CustomerID.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.SafeCustomer = entity.SafeCustomer.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponse =
                        CustomerConvertResponse.fromSafe(res);
                    const item: Customer = Customer.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getCountBySearch(
        query: string,
        singleSkillTagIds: string[],
        multiSkillTagIds: string[],
        occupationSkillTagCategory: entity.OccupationSkillTag.Category,
        occupationSkillTagId: string,
        gender: entity.User.Gender,
        age: api.CustomerAgeSearchType,
        workIntention: entity.Customer.WorkIntention,
        fullTimePossibility: entity.Customer.FullTimePossibility
    ): Promise<number | Long> {
        return new Promise<number | Long>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: SearchCustomerCountRequest =
                        new SearchCustomerCountRequest();
                    req.query = query;
                    req.singleSkillTagIDs = singleSkillTagIds;
                    req.multiSkillTagIDs = multiSkillTagIds;
                    req.occupationSkillTagCategory = occupationSkillTagCategory;
                    req.occupationSkillTagID = occupationSkillTagId;
                    req.gender = gender;
                    req.age = age;
                    req.workIntention = workIntention;
                    req.fullTimePossibility = fullTimePossibility;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetCountBySearch",
                        token,
                        SearchCustomerCountRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: Count = Count.decode(binary);
                    const item: number | Long = res.count;
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getCountByOccupation(
        customerCountByOccupationRequest: TCustomerCountByOccupationRequest
    ): Promise<number | Long> {
        return new Promise<number | Long>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CustomerCountByOccupationRequest =
                        new CustomerCountByOccupationRequest();
                    req.occupation = customerCountByOccupationRequest.occupation;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetCountByOccupation",
                        token,
                        CustomerCountByOccupationRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: Count = Count.decode(binary);
                    const item: number | Long = res.count;
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getForScout(customerID: string): Promise<Customer> {
        return new Promise<Customer>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CustomerID = new CustomerID();
                    req.id = customerID;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetForScout",
                        token,
                        CustomerID.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.SafeCustomer = entity.SafeCustomer.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponse =
                        CustomerConvertResponse.fromSafe(res);
                    const item: Customer = Customer.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public create(
        createCustomerRequest: TCreateCustomerRequest
    ): Promise<Customer> {
        return new Promise<Customer>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    // birthdate: Frontend => BackEnd における型変換
                    const birthdate_string: string = CustomerAPI.birthdateConvertToString(
                        createCustomerRequest.birthdate
                    );

                    const req: CreateCustomerRequest = new CreateCustomerRequest();
                    req.name = createCustomerRequest.name;
                    req.nameKana = createCustomerRequest.nameKana;
                    req.gender = createCustomerRequest.gender;
                    req.phoneNumber = createCustomerRequest.phoneNumber;
                    req.birthdate = birthdate_string;
                    req.routeInfo = createCustomerRequest.routeInfo;
                    req.facebookURL = createCustomerRequest.facebookURL;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/Create",
                        token,
                        CreateCustomerRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.Customer = entity.Customer.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponse =
                        CustomerConvertResponse.from(res);
                    const item: Customer = Customer.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public createSuperEngineer(
        createCustomerRequest: TCreateCustomerRequest
    ): Promise<Customer> {
        return new Promise<Customer>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: api.CreateSuperEngineerRequest =
                        new api.CreateSuperEngineerRequest();
                    req.name = createCustomerRequest.name;
                    req.nameKana = createCustomerRequest.nameKana;
                    req.gender = createCustomerRequest.gender;
                    req.phoneNumber = createCustomerRequest.phoneNumber;
                    req.portfolioURL = createCustomerRequest.portfolio;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/CreateSuperEngineer",
                        token,
                        api.CreateSuperEngineerRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.Customer = entity.Customer.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponse =
                        CustomerConvertResponse.from(res);
                    const item: Customer = Customer.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public createPro(
        createCustomerRequest: TCreateCustomerRequest
    ): Promise<Customer> {
        return new Promise<Customer>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: api.CreateProRequest = new api.CreateProRequest();
                    req.name = createCustomerRequest.name;
                    req.nameKana = createCustomerRequest.nameKana;
                    req.gender = createCustomerRequest.gender;
                    req.phoneNumber = createCustomerRequest.phoneNumber;
                    req.portfolioURL = createCustomerRequest.portfolio;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/CreatePro",
                        token,
                        api.CreateProRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.Customer = entity.Customer.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponse =
                        CustomerConvertResponse.from(res);
                    const item: Customer = Customer.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public update(
        updateCustomerParams: TUpdateCustomerParams
    ): Promise<Customer> {
        return new Promise<Customer>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    // birthdate: Frontend => BackEnd における型変換
                    const birthdate_string: string = CustomerAPI.birthdateConvertToString(
                        updateCustomerParams.birthdate
                    );

                    const req: UpdateCustomerRequest = new UpdateCustomerRequest();
                    req.name = updateCustomerParams.name;
                    req.nameKana = updateCustomerParams.nameKana;
                    req.iconURL = updateCustomerParams.iconURL;
                    req.birthdate = birthdate_string;
                    req.gender = updateCustomerParams.gender;
                    req.phoneNumber = updateCustomerParams.phoneNumber;
                    req.pr = updateCustomerParams.pr;
                    req.address = updateCustomerParams.address;
                    req.academicBackgrounds = updateCustomerParams.academicBackgrounds;
                    req.requirements = updateCustomerParams.requirements;
                    req.languages = updateCustomerParams.languages;
                    req.moreLanguage = updateCustomerParams.moreLanguage;
                    req.speakingLevel = updateCustomerParams.speakingLevel;
                    req.resumeURL = updateCustomerParams.resumeURL;
                    req.jobHistoryURL = updateCustomerParams.jobHistoryURL;
                    req.workingFrequency = updateCustomerParams.workingFrequency;
                    req.workIntention = updateCustomerParams.workIntention;
                    req.requestedFee = updateCustomerParams.requestedFee;
                    req.requestedCondition = updateCustomerParams.requestedCondition;
                    req.dutySummary = updateCustomerParams.dutySummary;
                    req.fullTimePossibility = updateCustomerParams.fullTimePossibility;
                    req.brokerageProjectPossibility =
                        updateCustomerParams.brokerageProjectPossibility;
                    req.portfolioURL = updateCustomerParams.portfolio;
                    req.birthplace = updateCustomerParams.birthplace;
                    req.facebookURL = updateCustomerParams.facebookURL;
                    req.linkedInURL = updateCustomerParams.linkedInURL;
                    req.xURL = updateCustomerParams.xURL;


                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/Update",
                        token,
                        UpdateCustomerRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: entity.Customer = entity.Customer.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponse =
                        CustomerConvertResponse.from(res);
                    const item: Customer = Customer.from(resConverted);
                    resolve(item);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public updateMultiSkillTag(ids: string[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: api.UpdateCustomerMultiSkillTagRequest =
                        new api.UpdateCustomerMultiSkillTagRequest();
                    req.tagIDs = ids;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/UpdateMultiSkillTag",
                        token,
                        api.UpdateCustomerMultiSkillTagRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    resolve();
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public generatePDF(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            reject(new Error("Not implemented"));
        });
    }

    public getRecommmendByMatch(id: string): Promise<Customer[]> {
        return new Promise<Customer[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CustomerID = new CustomerID();
                    req.id = id;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetRecommendByMatch",
                        token,
                        CustomerID.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CustomerList = CustomerList.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponseList =
                        CustomerConvertResponseList.from(res);
                    const items: Customer[] = resConverted.items.map(
                        (item: CustomerConvertResponse): Customer => {
                            return Customer.from(item);
                        }
                    );
                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public getRecommmendByTrend(id: string): Promise<Customer[]> {
        return new Promise<Customer[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: CustomerID = new CustomerID();
                    req.id = id;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GetRecommendByTrend",
                        token,
                        CustomerID.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CustomerList = CustomerList.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponseList =
                        CustomerConvertResponseList.from(res);
                    const items: Customer[] = resConverted.items.map(
                        (item: CustomerConvertResponse): Customer => {
                            return Customer.from(item);
                        }
                    );
                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }

    public generatePR = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: common.Empty = new common.Empty();
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GeneratePR",
                        token,
                        common.Empty.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res = common.TextResult.decode(binary);
                    resolve(res.result);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    };

    public generateDutySummary = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: common.Empty = new common.Empty();
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/GenerateDutySummary",
                        token,
                        common.Empty.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res = common.TextResult.decode(binary);
                    resolve(res.result);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    };

    public agenticSearch(query: string): Promise<Customer[]> {
        return new Promise<Customer[]>((resolve, reject) => {
            getToken()
                .then((token: string): Promise<Uint8Array> => {
                    const req: AgenticSearchRequest = new AgenticSearchRequest();
                    req.query = query;
                    const writer: BufferWriter | Writer = Writer.create();
                    return this.apiClient.postWithToken(
                        "/api.CustomerService/AgenticSearch",
                        token,
                        AgenticSearchRequest.encode(req, writer).finish()
                    );
                })
                .then((binary: Uint8Array): void => {
                    const res: CustomerList = CustomerList.decode(binary);
                    // BE=>FE 型変換
                    const resConverted: CustomerConvertResponseList =
                        CustomerConvertResponseList.from(res);
                    const items: Customer[] = resConverted.items.map(
                        (item: CustomerConvertResponse): Customer => {
                            return Customer.from(item);
                        }
                    );
                    resolve(items);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    }
}

const createCustomerAPI = (apiClient: IApiClient): ICustomerRepository => {
    return new CustomerAPI(apiClient);
};

export {createCustomerAPI};



// WEBPACK FOOTER //
// ./src/infra/api/service/customer_api.ts